import _Vue from 'vue'; // <-- notice the changed import
import { isBadRequestResult, } from '~/api/error-helpers';

type Router = import('vue-router').default;
type Store = import ('./store').StoreType;
type Message = import ('./store').Message;

function remainingTimeAuthenticatedInMilliseconds(store: Store) {
	if (!store.state.accessTokenExpiration) {
		return 0;
	}

	return (store.state.accessTokenExpiration.valueOf() - new Date().valueOf());
}

export class AuthService {
	constructor(router: Router, store: Store) {
		if (!router) {
			throw new Error('Must provide a router instance');
		}
		if (!store) {
			throw new Error('Must provide a store instance');
		}

		this._Router = router;
		this._Store = store;
	}

	public async checkSession() {
		if (!this._Store.state.prefetchedCustomerDetails || this._Store.state.businessPartnerDetails == null || this._Store.state.accessTokenExpiration == null) {
			return;
		}

		const expirationPromise = (remainingTimeAuthenticatedInMilliseconds(this._Store) <= 30 * 1000)
			? _Vue.selfServeApi.loginTRefreshCreate()
			: _Vue.selfServeApi.loginTimeRemainingList().catch((error) => {
				if (isBadRequestResult(error) && error.status === 401) {
					return _Vue.selfServeApi.loginTRefreshCreate();
				}
				throw error;
			});

		try {
			const { result, } = await expirationPromise;
			this._Store.commit.updateAccessExpiration(new Date(result.accessTokenExpiration));
		} catch (e) {
			console.warn('Failed on session checking', e);
		}

		if (remainingTimeAuthenticatedInMilliseconds(this._Store) <= 0) {
			await this.logout({ type: 'Warning', body: 'For security purposes you have been logged out', });
		}
	}

	public async logout(message: Omit<Message, 'status'>) {
		try {
			await this._Store.dispatch.logout();
			await this._Store.dispatch.resetAll();

			this._Store.commit.showMessage(message);

			await this._Router.replace('./login');
		} catch (e) {
			location.href = './login';
		}
	}

	private readonly _Router: Router;
	private readonly _Store: Store;
}

export interface AuthServicePluginOptions {
	router: Router;
	store: Store;
}

export function AuthServicePlugin(Vue: typeof _Vue, options: AuthServicePluginOptions): void {
	const authService = new AuthService(options.router, options.store);

	// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
	(Vue as any).authService = authService;
	// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
	Vue.prototype.$authService = authService;
}

declare module 'vue/types/vue' {
	interface Vue {
		$authService: AuthService;
	}
	interface VueConstructor {
		authService: AuthService;
	}
}
